import React, { useState, useEffect, useCallback } from "react";
import {
  Layers,
  Server,
  Cloud,
  GitBranch,
  Search,
  X,
  ChevronRight,
  Database,
} from "lucide-react";
import {
  getApplicationTopology,
  AppTopologyResponse,
  AppTopologyDeployment,
  AppTopologyDirectResource,
} from "../../../../api/frameworksApi";
import { showToast } from "../../../components/ui/custom-toast";
import { Tip } from "../../../components/ui/tooltip";
import { Dropdown } from "../../../components/Dropdown";
import { useTheme } from "next-themes";
import {
  ReactFlow,
  Background,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
  type ReactFlowInstance,
  type Node,
} from "@xyflow/react";

// ── Node sizes ────────────────────────────────────────────────────────────────

const AT_APP_W = 230;
const AT_APP_H = 92;
const AT_COMP_W = 172;
const AT_COMP_H = 68;
const AT_SVC_W = 182;
const AT_SVC_H = 72;
const AT_SVCCOMP_W = 162;
const AT_SVCCOMP_H = 62;
const AT_DEPLOY_W = 178;
const AT_DEPLOY_H = 66;
const AT_CLOUD_W = 168;
const AT_CLOUD_H = 72;
const AT_DIRRES_W = 164;
const AT_DIRRES_H = 60;
const AT_X_GAP = 20;
const AT_ROW_GAP = 60;

// Row Y positions
const ROW_MID = AT_APP_H + AT_ROW_GAP;
const ROW_SUB = ROW_MID + AT_COMP_H + AT_ROW_GAP;
const ROW_LEAF = ROW_SUB + AT_SVCCOMP_H + AT_ROW_GAP;
const ROW_CLOUD_RES = ROW_MID + AT_CLOUD_H + AT_ROW_GAP;

type FilterGroup = "all" | "components" | "services" | "cloud";

// ── Graph builder ─────────────────────────────────────────────────────────────

function buildAppGraph(
  topo: AppTopologyResponse,
  filterGroup: FilterGroup,
  filterItem: string, // "all" | specific name/title
): { nodes: Node[]; edges: any[] } {
  const nodes: Node[] = [];
  const edges: any[] = [];

  const compBandW = (comp: { deployments: AppTopologyDeployment[] }) => {
    const nd = comp.deployments.length;
    if (nd === 0) return AT_COMP_W;
    return Math.max(AT_COMP_W, nd * AT_DEPLOY_W + (nd - 1) * AT_X_GAP);
  };

  const svcBandW = (svc: {
    components: { deployments: AppTopologyDeployment[] }[];
  }) => {
    const nc = svc.components.length;
    if (nc === 0) return AT_SVC_W;
    const w =
      svc.components.reduce((s, c) => s + compBandW(c) + AT_X_GAP, 0) -
      AT_X_GAP;
    return Math.max(AT_SVC_W, w);
  };

  // Apply group + item filters
  const visComps =
    filterGroup === "all" || filterGroup === "components"
      ? topo.components.filter(
          (c) => filterItem === "all" || c.componentTitle === filterItem,
        )
      : [];
  const visSvcs =
    filterGroup === "all" || filterGroup === "services"
      ? topo.serviceDependencies.filter(
          (s) => filterItem === "all" || s.serviceName === filterItem,
        )
      : [];
  const visClouds =
    filterGroup === "all" || filterGroup === "cloud"
      ? topo.cloudAccounts.filter(
          (ca) => filterItem === "all" || ca.accountName === filterItem,
        )
      : [];

  type MidKind =
    | { kind: "comp"; data: (typeof topo.components)[0]; idx: number }
    | { kind: "svc"; data: (typeof topo.serviceDependencies)[0]; idx: number }
    | { kind: "cloud"; data: (typeof topo.cloudAccounts)[0]; idx: number };

  const midItems: MidKind[] = [
    ...visComps.map((c) => ({
      kind: "comp" as const,
      data: c,
      idx: topo.components.indexOf(c),
    })),
    ...visSvcs.map((s) => ({
      kind: "svc" as const,
      data: s,
      idx: topo.serviceDependencies.indexOf(s),
    })),
    ...visClouds.map((ca) => ({
      kind: "cloud" as const,
      data: ca,
      idx: topo.cloudAccounts.indexOf(ca),
    })),
  ];

  const cloudBandW = (ca: (typeof topo.cloudAccounts)[0]): number => {
    const nd = (ca.directResources ?? []).length;
    if (nd === 0) return AT_CLOUD_W;
    return Math.max(AT_CLOUD_W, nd * AT_DIRRES_W + (nd - 1) * AT_X_GAP);
  };

  const getBandW = (item: MidKind): number => {
    if (item.kind === "comp") return compBandW(item.data);
    if (item.kind === "svc") return svcBandW(item.data);
    return cloudBandW(item.data);
  };

  const totalW =
    midItems.reduce((s, it) => s + getBandW(it) + AT_X_GAP, 0) - AT_X_GAP;

  // App root node (always shown)
  nodes.push({
    id: "app-root",
    type: "atAppNode",
    position: { x: Math.max(0, totalW / 2 - AT_APP_W / 2), y: 0 },
    data: {
      name: topo.name,
      status: topo.appStatus,
      criticality: topo.criticality,
      totalComponents: topo.totalComponents,
      totalDeployments: topo.totalDeployments,
    },
  });

  let curX = 0;
  midItems.forEach((item) => {
    const bw = getBandW(item);

    if (item.kind === "comp") {
      const comp = item.data;
      const compId = `comp-${item.idx}`;
      const compX = curX + bw / 2 - AT_COMP_W / 2;

      nodes.push({
        id: compId,
        type: "atCompNode",
        position: { x: compX, y: ROW_MID },
        data: { title: comp.componentTitle, compType: comp.componentType },
      });
      edges.push({
        id: `e-root-${compId}`,
        source: "app-root",
        target: compId,
        type: "smoothstep",
        style: { stroke: "#6366f1", strokeWidth: 1.5 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#6366f1",
          width: 12,
          height: 12,
        },
      });

      if (comp.deployments.length > 0) {
        const depTW =
          comp.deployments.length * AT_DEPLOY_W +
          (comp.deployments.length - 1) * AT_X_GAP;
        const depSX = curX + bw / 2 - depTW / 2;
        comp.deployments.forEach((dep, di) => {
          const depId = `dep-${item.idx}-${di}`;
          nodes.push({
            id: depId,
            type: "atDeployNode",
            position: { x: depSX + di * (AT_DEPLOY_W + AT_X_GAP), y: ROW_SUB },
            data: { ...dep },
          });
          const ec = dep.ciType === "CLOUD" ? "#f59e0b" : "#475569";
          edges.push({
            id: `e-${compId}-${depId}`,
            source: compId,
            target: depId,
            type: "smoothstep",
            style: { stroke: ec, strokeWidth: 1 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: ec,
              width: 10,
              height: 10,
            },
          });
        });
      }
    }

    if (item.kind === "svc") {
      const svc = item.data;
      const svcId = `svc-${item.idx}`;
      const svcX = curX + bw / 2 - AT_SVC_W / 2;

      nodes.push({
        id: svcId,
        type: "atSvcDepNode",
        position: { x: svcX, y: ROW_MID },
        data: {
          name: svc.serviceName,
          status: svc.serviceStatus,
          depType: svc.dependencyType,
        },
      });
      edges.push({
        id: `e-root-${svcId}`,
        source: "app-root",
        target: svcId,
        type: "smoothstep",
        style: { stroke: "#a855f7", strokeWidth: 1.5 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#a855f7",
          width: 12,
          height: 12,
        },
      });

      let svcCompCurX = curX;
      svc.components.forEach((comp, ci) => {
        const cbw = compBandW(comp);
        const svcCompId = `svc-${item.idx}-comp-${ci}`;
        const svcCompX = svcCompCurX + cbw / 2 - AT_SVCCOMP_W / 2;

        nodes.push({
          id: svcCompId,
          type: "atSvcCompNode",
          position: { x: svcCompX, y: ROW_SUB },
          data: { title: comp.componentTitle, compType: comp.componentType },
        });
        edges.push({
          id: `e-${svcId}-${svcCompId}`,
          source: svcId,
          target: svcCompId,
          type: "smoothstep",
          style: { stroke: "#7c3aed", strokeWidth: 1 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "#7c3aed",
            width: 10,
            height: 10,
          },
        });

        if (comp.deployments.length > 0) {
          const depTW =
            comp.deployments.length * AT_DEPLOY_W +
            (comp.deployments.length - 1) * AT_X_GAP;
          const depSX = svcCompCurX + cbw / 2 - depTW / 2;
          comp.deployments.forEach((dep, di) => {
            const depId = `svc-${item.idx}-comp-${ci}-dep-${di}`;
            nodes.push({
              id: depId,
              type: "atDeployNode",
              position: {
                x: depSX + di * (AT_DEPLOY_W + AT_X_GAP),
                y: ROW_LEAF,
              },
              data: { ...dep },
            });
            const ec = dep.ciType === "CLOUD" ? "#f59e0b" : "#475569";
            edges.push({
              id: `e-${svcCompId}-${depId}`,
              source: svcCompId,
              target: depId,
              type: "smoothstep",
              style: { stroke: ec, strokeWidth: 1 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: ec,
                width: 10,
                height: 10,
              },
            });
          });
        }

        svcCompCurX += cbw + AT_X_GAP;
      });
    }

    if (item.kind === "cloud") {
      const ca = item.data;
      const cloudId = `cloud-${item.idx}`;
      const cloudX = curX + bw / 2 - AT_CLOUD_W / 2;
      const directRes = ca.directResources ?? [];
      const hasChildren = directRes.length > 0;

      nodes.push({
        id: cloudId,
        type: "atCloudAccNode",
        position: { x: cloudX, y: ROW_MID },
        data: { ...ca, hasChildren },
      });
      edges.push({
        id: `e-root-${cloudId}`,
        source: "app-root",
        target: cloudId,
        type: "smoothstep",
        style: { stroke: "#f59e0b", strokeWidth: 1.5 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#f59e0b",
          width: 12,
          height: 12,
        },
      });

      if (hasChildren) {
        const resTW =
          directRes.length * AT_DIRRES_W + (directRes.length - 1) * AT_X_GAP;
        const resSX = curX + bw / 2 - resTW / 2;
        directRes.forEach((res: AppTopologyDirectResource, ri: number) => {
          const resId = `cloud-${item.idx}-res-${ri}`;
          nodes.push({
            id: resId,
            type: "atDirectResNode",
            position: {
              x: resSX + ri * (AT_DIRRES_W + AT_X_GAP),
              y: ROW_CLOUD_RES,
            },
            data: { ...res },
          });
          edges.push({
            id: `e-${cloudId}-${resId}`,
            source: cloudId,
            target: resId,
            type: "smoothstep",
            style: { stroke: "#f59e0b88", strokeWidth: 1 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#f59e0b",
              width: 10,
              height: 10,
            },
          });
        });
      }
    }

    curX += bw + AT_X_GAP;
  });

  return { nodes, edges };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS_AT: Record<string, string> = {
  OPERATIONAL: "#22c55e",
  ACTIVE: "#22c55e",
  RUNNING: "#22c55e",
  UNDER_DEVELOPMENT: "#eab308",
  DISPOSITION: "#f87171",
  INACTIVE: "#94a3b8",
  OTHER: "#94a3b8",
};

const CRIT_COLORS: Record<string, string> = {
  CRITICAL: "#ef4444",
  HIGH: "#f97316",
  MEDIUM: "#eab308",
  LOW: "#22c55e",
};

function statusDotColor(status: string | undefined): string {
  const s = (status || "").toLowerCase();
  return ["running", "active", "available"].includes(s)
    ? "#22c55e"
    : ["stopped", "failed", "terminated"].includes(s)
      ? "#ef4444"
      : s
        ? "#eab308"
        : "#475569";
}

// ── Node components ───────────────────────────────────────────────────────────

function AtAppNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  const sc = STATUS_COLORS_AT[data.status] ?? "#94a3b8";
  const cc = CRIT_COLORS[data.criticality] ?? "#94a3b8";
  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#1e1b4b,#312e81)"
          : "linear-gradient(135deg,#eef2ff,#ffffff)",
        border: dk ? "2px solid #818cf8" : "2px solid #6366f1",
        borderRadius: 14,
        padding: "12px 18px",
        width: AT_APP_W,
        boxShadow: dk
          ? "0 0 28px rgba(129,140,248,0.22),0 6px 24px rgba(0,0,0,0.6)"
          : "0 0 20px rgba(99,102,241,0.14),0 6px 20px rgba(15,23,42,0.12)",
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "#818cf8",
          border: dk ? "2px solid #1e1b4b" : "2px solid #ffffff",
          width: 8,
          height: 8,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 7,
        }}
      >
        <Layers
          className="size-4 flex-shrink-0"
          style={{ color: dk ? "#a5b4fc" : "#6366f1" }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: dk ? "#e0e7ff" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.name}
        </span>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 9,
            background: `${sc}22`,
            color: sc,
            padding: "2px 7px",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          {data.status}
        </span>
        <span
          style={{
            fontSize: 9,
            background: `${cc}22`,
            color: cc,
            padding: "2px 7px",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          {data.criticality}
        </span>
        <span
          style={{
            fontSize: 9,
            background: dk ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
            color: dk ? "#94a3b8" : "#64748b",
            padding: "2px 7px",
            borderRadius: 999,
          }}
        >
          {data.totalComponents} comp · {data.totalDeployments} deploy
        </span>
      </div>
    </div>
  );
}

function AtCompNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#071628,#0f2749)"
          : "linear-gradient(135deg,#f0f9ff,#ffffff)",
        border: dk ? "1.5px solid #38bdf8" : "1.5px solid #0ea5e9",
        borderRadius: 10,
        padding: "10px 14px",
        width: AT_COMP_W,
        boxShadow: dk
          ? "0 0 18px rgba(56,189,248,0.14),0 4px 16px rgba(0,0,0,0.5)"
          : "0 0 14px rgba(14,165,233,0.1),0 4px 14px rgba(15,23,42,0.1)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#38bdf8",
          border: dk ? "2px solid #0f172a" : "2px solid #ffffff",
          width: 7,
          height: 7,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "#38bdf8",
          border: dk ? "2px solid #0f172a" : "2px solid #ffffff",
          width: 7,
          height: 7,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 5,
        }}
      >
        <Server
          className="size-3.5 flex-shrink-0"
          style={{ color: dk ? "#38bdf8" : "#0ea5e9" }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 12,
            color: dk ? "#e2e8f0" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {data.title}
        </span>
      </div>
      <span
        style={{
          fontSize: 9,
          background: dk ? "rgba(56,189,248,0.12)" : "rgba(14,165,233,0.1)",
          color: dk ? "#7dd3fc" : "#0369a1",
          padding: "1px 6px",
          borderRadius: 4,
          fontWeight: 600,
        }}
      >
        {data.compType}
      </span>
    </div>
  );
}

function AtSvcDepNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  const depColor = data.depType === "REQUIRED" ? "#f97316" : "#a855f7";
  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#160b2a,#241347)"
          : "linear-gradient(135deg,#faf5ff,#ffffff)",
        border: dk ? "1.5px solid #a855f7" : "1.5px solid #9333ea",
        borderRadius: 10,
        padding: "10px 14px",
        width: AT_SVC_W,
        boxShadow: dk
          ? "0 0 18px rgba(168,85,247,0.14),0 4px 16px rgba(0,0,0,0.5)"
          : "0 0 14px rgba(147,51,234,0.1),0 4px 14px rgba(15,23,42,0.1)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#a855f7",
          border: dk ? "2px solid #0f172a" : "2px solid #ffffff",
          width: 7,
          height: 7,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: "#a855f7",
          border: dk ? "2px solid #0f172a" : "2px solid #ffffff",
          width: 7,
          height: 7,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 5,
        }}
      >
        <GitBranch
          className="size-3.5 flex-shrink-0"
          style={{ color: dk ? "#c084fc" : "#9333ea" }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 12,
            color: dk ? "#e9d5ff" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {data.name}
        </span>
      </div>
      <div style={{ display: "flex", gap: 5 }}>
        <span
          style={{
            fontSize: 9,
            background: `${depColor}22`,
            color: depColor,
            padding: "1px 6px",
            borderRadius: 4,
            fontWeight: 700,
          }}
        >
          {data.depType}
        </span>
        {data.status && (
          <span
            style={{
              fontSize: 9,
              background: dk ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
              color: dk ? "#64748b" : "#64748b",
              padding: "1px 6px",
              borderRadius: 4,
            }}
          >
            {data.status}
          </span>
        )}
      </div>
    </div>
  );
}

function AtSvcCompNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#050e1a,#0b1d34)"
          : "linear-gradient(135deg,#f0f9ff,#ffffff)",
        border: dk ? "1px solid #1e3a5f" : "1px solid #bae6fd",
        borderRadius: 8,
        padding: "8px 12px",
        width: AT_SVCCOMP_W,
        boxShadow: dk
          ? "0 2px 12px rgba(0,0,0,0.45)"
          : "0 2px 10px rgba(15,23,42,0.08)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: dk ? "#1e3a5f" : "#38bdf8",
          border: dk ? "1px solid #38bdf8" : "1px solid #ffffff",
          width: 6,
          height: 6,
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: dk ? "#1e3a5f" : "#38bdf8",
          border: dk ? "1px solid #38bdf8" : "1px solid #ffffff",
          width: 6,
          height: 6,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        <Server
          className="size-3"
          style={{ color: dk ? "#38bdf8" : "#0ea5e9", flexShrink: 0 }}
        />
        <span
          style={{
            fontWeight: 600,
            fontSize: 11,
            color: dk ? "#cbd5e1" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {data.title}
        </span>
      </div>
      <span style={{ fontSize: 9, color: dk ? "#334155" : "#64748b" }}>
        {data.compType}
      </span>
    </div>
  );
}

function AtDeployNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  const isCloud = data.ciType === "CLOUD";
  const accent = isCloud ? "#f59e0b" : "#64748b";
  const bg = dk
    ? isCloud
      ? "linear-gradient(135deg,#1c1400,#292000)"
      : "linear-gradient(135deg,#0f1623,#1a2433)"
    : isCloud
      ? "linear-gradient(135deg,#fffbeb,#ffffff)"
      : "linear-gradient(135deg,#f8fafc,#ffffff)";
  const name = isCloud
    ? data.cloudResourceName || data.cloudIdentifier || "Cloud Resource"
    : data.ciName || "Unknown";
  const sc = (data.status || "").toLowerCase();
  const isRunning = ["running", "active", "available"].includes(sc);
  const dotColor = isRunning
    ? "#22c55e"
    : ["stopped", "failed", "terminated"].includes(sc)
      ? "#ef4444"
      : sc
        ? "#eab308"
        : "#475569";

  return (
    <div
      style={{
        background: bg,
        border: dk ? `1px solid ${accent}44` : `1px solid ${accent}66`,
        borderRadius: 8,
        padding: "8px 10px",
        width: AT_DEPLOY_W,
        boxShadow: dk
          ? "0 2px 10px rgba(0,0,0,0.5)"
          : "0 2px 8px rgba(15,23,42,0.08)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: accent,
          border: dk
            ? `2px solid ${isCloud ? "#1c1400" : "#0f1623"}`
            : "2px solid #ffffff",
          width: 6,
          height: 6,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        {isCloud ? (
          <Cloud
            className="size-3.5 flex-shrink-0"
            style={{ color: "#fbbf24" }}
          />
        ) : (
          <Server
            className="size-3.5 flex-shrink-0"
            style={{ color: "#64748b" }}
          />
        )}
        <span
          style={{
            fontWeight: 600,
            fontSize: 11,
            color: dk ? (isCloud ? "#fde68a" : "#cbd5e1") : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {name}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          flexWrap: "wrap",
        }}
      >
        {data.ciIpAddress && (
          <span
            style={{ fontSize: 9, fontFamily: "monospace", color: "#64748b" }}
          >
            {data.ciIpAddress}
          </span>
        )}
        {data.port && (
          <span
            style={{
              fontSize: 9,
              background: dk ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)",
              color: "#475569",
              padding: "1px 5px",
              borderRadius: 3,
              fontFamily: "monospace",
            }}
          >
            :{data.port}
          </span>
        )}
        {data.role && (
          <span
            style={{
              fontSize: 9,
              background: dk ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
              color: "#475569",
              padding: "1px 5px",
              borderRadius: 3,
            }}
          >
            {data.role}
          </span>
        )}
        {data.status && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              marginLeft: "auto",
            }}
          >
            {/* Blinking dot for running/active */}
            <span
              style={{
                position: "relative",
                width: 7,
                height: 7,
                flexShrink: 0,
              }}
            >
              {isRunning && (
                <span
                  className="at-ping-ring"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: dotColor,
                  }}
                />
              )}
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: dotColor,
                }}
              />
            </span>
            <span style={{ fontSize: 9, color: dotColor }}>{data.status}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function AtCloudAccNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  const directCount = (data.directResources ?? []).length;
  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#1c1400,#292000)"
          : "linear-gradient(135deg,#fffbeb,#ffffff)",
        border: dk ? "1.5px solid #f59e0b" : "1.5px solid #d97706",
        borderRadius: 10,
        padding: "10px 14px",
        width: AT_CLOUD_W,
        boxShadow: dk
          ? "0 0 16px rgba(245,158,11,0.14),0 4px 16px rgba(0,0,0,0.5)"
          : "0 0 14px rgba(217,119,6,0.1),0 4px 14px rgba(15,23,42,0.1)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#f59e0b",
          border: dk ? "2px solid #1c1400" : "2px solid #ffffff",
          width: 7,
          height: 7,
        }}
      />
      {data.hasChildren && (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{
            background: "#f59e0b",
            border: dk ? "2px solid #1c1400" : "2px solid #ffffff",
            width: 7,
            height: 7,
          }}
        />
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 5,
        }}
      >
        <Cloud
          className="size-3.5 flex-shrink-0"
          style={{ color: dk ? "#fbbf24" : "#d97706" }}
        />
        <span
          style={{
            fontWeight: 700,
            fontSize: 12,
            color: dk ? "#fde68a" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {data.accountName}
        </span>
      </div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
        <span
          style={{
            fontSize: 9,
            background: "rgba(245,158,11,0.15)",
            color: "#f59e0b",
            padding: "1px 6px",
            borderRadius: 999,
            fontWeight: 700,
          }}
        >
          {data.provider}
        </span>
        <span
          style={{
            fontSize: 9,
            background: dk ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
            color: "#64748b",
            padding: "1px 6px",
            borderRadius: 999,
          }}
        >
          {data.region}
        </span>
        <span
          style={{
            fontSize: 9,
            background: dk ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
            color: dk ? "#94a3b8" : "#64748b",
            padding: "1px 6px",
            borderRadius: 999,
          }}
        >
          {data.resourceCount} res
        </span>
        {directCount > 0 && (
          <span
            style={{
              fontSize: 9,
              background: "rgba(251,191,36,0.18)",
              color: "#fbbf24",
              padding: "1px 6px",
              borderRadius: 999,
              fontWeight: 700,
            }}
          >
            {directCount} mapped
          </span>
        )}
      </div>
    </div>
  );
}

function AtDirectResNode({ data }: { data: any }) {
  const sc = (data.status || "").toLowerCase();
  const dotColor = ["running", "active", "available"].includes(sc)
    ? "#22c55e"
    : ["stopped", "failed", "terminated"].includes(sc)
      ? "#ef4444"
      : sc
        ? "#eab308"
        : "#475569";
  const isRunning = ["running", "active", "available"].includes(sc);
  const dk = data.__isDark !== false;

  return (
    <div
      style={{
        background: dk
          ? "linear-gradient(135deg,#1a1200,#221800)"
          : "linear-gradient(135deg,#fffbeb,#ffffff)",
        border: dk ? "1px solid #f59e0b55" : "1px solid #f59e0b66",
        borderRadius: 8,
        padding: "8px 10px",
        width: AT_DIRRES_W,
        boxShadow: dk
          ? "0 2px 10px rgba(0,0,0,0.5)"
          : "0 2px 8px rgba(15,23,42,0.08)",
        opacity: data.__dim ? 0.15 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: "#f59e0b",
          border: dk ? "2px solid #1a1200" : "2px solid #ffffff",
          width: 6,
          height: 6,
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 4,
        }}
      >
        <Database
          className="size-3 flex-shrink-0"
          style={{ color: dk ? "#f59e0b" : "#d97706" }}
        />
        <span
          style={{
            fontWeight: 600,
            fontSize: 11,
            color: dk ? "#fde68a" : "#0f172a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {data.resourceName || data.resourceId}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: 9,
            background: "rgba(245,158,11,0.12)",
            color: "#92400e",
            padding: "1px 5px",
            borderRadius: 3,
          }}
        >
          {data.serviceName}
        </span>
        {data.region && (
          <span
            style={{ fontSize: 9, color: "#57534e", fontFamily: "monospace" }}
          >
            {data.region}
          </span>
        )}
        {data.status && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              marginLeft: "auto",
            }}
          >
            <span
              style={{
                position: "relative",
                width: 7,
                height: 7,
                flexShrink: 0,
              }}
            >
              {isRunning && (
                <span
                  className="at-ping-ring"
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: dotColor,
                  }}
                />
              )}
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: dotColor,
                }}
              />
            </span>
            <span style={{ fontSize: 9, color: dotColor }}>{data.status}</span>
          </span>
        )}
      </div>
    </div>
  );
}

const atNodeTypes = {
  atAppNode: AtAppNode,
  atCompNode: AtCompNode,
  atSvcDepNode: AtSvcDepNode,
  atSvcCompNode: AtSvcCompNode,
  atDeployNode: AtDeployNode,
  atCloudAccNode: AtCloudAccNode,
  atDirectResNode: AtDirectResNode,
};

// ── Injected CSS — controls + blinking dot ────────────────────────────────────

const AT_GLOBAL_CSS = [
  "@keyframes at-ping { 0%,100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(2.2); opacity: 0; } }",
  ".at-ping-ring { animation: at-ping 1.4s ease-out infinite; }",
].join(" ");

// ── Floating controls (matches the Cloud Accounts topology view) ───────────────

function AtTopologyControls({
  onZoomIn,
  onZoomOut,
  onFitView,
  onFullscreen,
  locked,
  onToggleLock,
  isFullscreen,
  isDark,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onFullscreen: () => void;
  locked: boolean;
  onToggleLock: () => void;
  isFullscreen: boolean;
  isDark: boolean;
}) {
  const btns = [
    { icon: "+", title: "Zoom In", action: onZoomIn },
    { icon: "−", title: "Zoom Out", action: onZoomOut },
    { icon: "⊡", title: "Fit to Screen", action: onFitView },
    {
      icon: isFullscreen ? "⊠" : "⊞",
      title: isFullscreen ? "Exit Fullscreen" : "Fullscreen",
      action: onFullscreen,
    },
    {
      icon: locked ? "🔒︎" : "🔓︎",
      title: locked ? "Unlock Layout" : "Lock Layout",
      action: onToggleLock,
    },
  ];
  const panelBg = isDark ? "rgba(7,14,30,0.92)" : "rgba(255,255,255,0.95)";
  const panelBorder = isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0";
  const btnBg = isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6";
  const btnBorder = isDark ? "rgba(255,255,255,0.1)" : "#d1d5db";
  const btnColor = isDark ? "#e2e8f0" : "#111827";
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: panelBg,
        border: `1px solid ${panelBorder}`,
        borderRadius: 12,
        padding: "8px 6px",
        backdropFilter: "blur(8px)",
        boxShadow: isDark
          ? "0 8px 32px rgba(0,0,0,0.5)"
          : "0 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      {btns.map((b) => (
        <Tip key={b.title} label={b.title} side="top">
          <button
            onClick={b.action}
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              border: `1px solid ${btnBorder}`,
              background: btnBg,
              color: btnColor,
              cursor: "pointer",
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                "rgba(99,102,241,0.2)";
              (e.currentTarget as HTMLButtonElement).style.color = "#6366f1";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = btnBg;
              (e.currentTarget as HTMLButtonElement).style.color = btnColor;
            }}
          >
            {b.icon}
          </button>
        </Tip>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function AppTopologyView({ appId }: { appId: string }) {
  const { theme } = useTheme();
  const isDark = theme !== "light";
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [locked, setLocked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [topo, setTopo] = useState<AppTopologyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowRef, setFlowRef] = useState<ReactFlowInstance | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterGroup, setFilterGroup] = useState<FilterGroup>("all");
  const [filterItem, setFilterItem] = useState<string>("all");
  const nodesRef = React.useRef<typeof nodes>([]);
  nodesRef.current = nodes;

  // Rebuild graph whenever filters change
  useEffect(() => {
    if (!topo) return;
    const { nodes: n, edges: e } = buildAppGraph(topo, filterGroup, filterItem);
    setNodes(
      (n as any[]).map((nd) => ({
        ...nd,
        data: { ...nd.data, __isDark: isDark },
      })) as any,
    );
    setEdges(e as any);
    setTimeout(() => flowRef?.fitView({ padding: 0.25, duration: 350 }), 80);
  }, [topo, filterGroup, filterItem, isDark]);

  useEffect(() => {
    setLoading(true);
    getApplicationTopology(appId)
      .then((res) => setTopo(res.data))
      .catch(() => showToast("error", "Failed to load application topology"))
      .finally(() => setLoading(false));
  }, [appId]);

  // Keep local fullscreen state in sync with the browser
  useEffect(() => {
    const onFsChange = () =>
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      el.requestFullscreen?.();
    }
  }, []);

  // Item options for the second filter dropdown, based on selected group
  const itemOptions: string[] = React.useMemo(() => {
    if (!topo) return [];
    if (filterGroup === "components")
      return topo.components.map((c) => c.componentTitle);
    if (filterGroup === "services")
      return topo.serviceDependencies.map((s) => s.serviceName);
    if (filterGroup === "cloud")
      return topo.cloudAccounts.map((ca) => ca.accountName);
    return [];
  }, [topo, filterGroup]);

  const handleGroupFilter = useCallback((g: FilterGroup) => {
    setFilterGroup(g);
    setFilterItem("all");
    setSearchQuery("");
  }, []);

  const handleItemFilter = useCallback(
    (item: string) => {
      setFilterItem(item);
      setSearchQuery("");
      setTimeout(() => flowRef?.fitView({ padding: 0.25, duration: 350 }), 120);
    },
    [flowRef],
  );

  const handleClearFilters = useCallback(() => {
    setFilterGroup("all");
    setFilterItem("all");
    setSearchQuery("");
    setTimeout(() => flowRef?.fitView({ padding: 0.25, duration: 350 }), 120);
  }, [flowRef]);

  const atNodeLabel = (n: Node): string =>
    [
      n.data.name,
      n.data.title,
      n.data.accountName,
      n.data.ciName,
      n.data.cloudResourceName,
      n.data.cloudIdentifier,
      n.data.resourceName,
      n.data.resourceId,
      n.data.serviceName,
      n.data.ciIpAddress,
      n.data.compType,
      n.data.provider,
      n.data.region,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const handleSearch = useCallback(
    (q: string) => {
      setSearchQuery(q);
      const query = q.trim().toLowerCase();
      setNodes((nds) =>
        nds.map((n) => {
          if (!query) return { ...n, data: { ...n.data, __dim: false } };
          // Root node always stays fully visible
          if (n.id === "app-root")
            return { ...n, data: { ...n.data, __dim: false } };
          return {
            ...n,
            data: { ...n.data, __dim: !atNodeLabel(n).includes(query) },
          };
        }),
      );
      // Scroll to first matching node (skip root)
      if (query) {
        const match = nodesRef.current.find(
          (n) => n.id !== "app-root" && atNodeLabel(n).includes(query),
        );
        if (match && flowRef) {
          const nw =
            match.type === "atCompNode"
              ? AT_COMP_W
              : match.type === "atSvcDepNode"
                ? AT_SVC_W
                : match.type === "atSvcCompNode"
                  ? AT_SVCCOMP_W
                  : match.type === "atDeployNode"
                    ? AT_DEPLOY_W
                    : match.type === "atCloudAccNode"
                      ? AT_CLOUD_W
                      : match.type === "atDirectResNode"
                        ? AT_DIRRES_W
                        : AT_APP_W;
          const nh =
            match.type === "atDeployNode"
              ? AT_DEPLOY_H
              : match.type === "atSvcCompNode"
                ? AT_SVCCOMP_H
                : match.type === "atDirectResNode"
                  ? AT_DIRRES_H
                  : AT_COMP_H;
          flowRef.setCenter(
            match.position.x + nw / 2,
            match.position.y + nh / 2,
            { duration: 500, zoom: 1.4 },
          );
        }
      }
    },
    [flowRef, setNodes],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="size-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!topo) return null;

  const hasData =
    topo.components.length > 0 ||
    topo.serviceDependencies.length > 0 ||
    topo.cloudAccounts.length > 0;
  if (!hasData) {
    return (
      <div className="py-16 text-center border border-dashed border-border rounded-xl">
        <Layers className="size-10 d mx-auto mb-3" />
        <p className="d text-sm">
          No topology data yet. Add components, service dependencies, or cloud
          accounts.
        </p>
      </div>
    );
  }

  const filtersActive = filterGroup !== "all" || filterItem !== "all";

  return (
    <div
      ref={containerRef}
      className="relative rounded-xl overflow-hidden"
      style={{
        height: isFullscreen ? "100vh" : "70vh",
        minHeight: 480,
        background: isDark ? "#07111f" : "#ffffff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
      }}
    >
      {/* Toolbar */}
      <div
        className="flex flex-col relative z-10"
        style={{
          background: isDark ? "rgba(7,17,31,0.96)" : "rgba(255,255,255,0.96)",
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
        }}
      >
        {/* Row 1 — stats + Fit View */}
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{ borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)"}` }}
        >
          <p style={{ fontSize: 11, color: isDark ? "#475569" : "#64748b" }}>
            <span
              style={{ fontWeight: 600, color: isDark ? "#94a3b8" : "#334155" }}
            >
              {nodes.length}
            </span>{" "}
            nodes ·{" "}
            <span style={{ fontWeight: 600, color: "#818cf8" }}>
              {topo.totalComponents}
            </span>{" "}
            <span style={{ color: "#818cf8" }}>components</span> ·{" "}
            <span style={{ fontWeight: 600, color: "#a855f7" }}>
              {topo.serviceDependencies.length}
            </span>{" "}
            <span style={{ color: "#a855f7" }}>service deps</span> ·{" "}
            <span style={{ fontWeight: 600, color: "#fbbf24" }}>
              {topo.totalCloudAccounts}
            </span>{" "}
            <span style={{ color: "#fbbf24" }}>cloud accounts</span>
            {(topo.totalDirectResources ?? 0) > 0 && (
              <>
                {" · "}
                <span style={{ fontWeight: 600, color: "#fb923c" }}>
                  {topo.totalDirectResources}
                </span>{" "}
                <span style={{ color: "#fb923c" }}>direct resources</span>
              </>
            )}
          </p>
        </div>

        {/* Row 2 — search + cascading filters */}
        <div className="flex items-center gap-2 px-4 py-2 flex-wrap">
          {/* Search */}
          <div style={{ position: "relative", width: 230 }}>
            <Search
              className="size-3.5"
              style={{
                position: "absolute",
                left: 9,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#475569",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search nodes…"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: 30,
                paddingRight: searchQuery ? 28 : 10,
                paddingTop: 5,
                paddingBottom: 5,
                fontSize: 12,
                background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
                borderRadius: 7,
                color: isDark ? "#e2e8f0" : "#0f172a",
                outline: "none",
              }}
            />
            {searchQuery && (
              <button
                onClick={() => handleSearch("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#475569",
                  padding: 0,
                }}
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <ChevronRight
            className="size-3.5 flex-shrink-0"
            style={{ color: "#1e3a5f" }}
          />

          {/* Filter Group */}
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Layers
              className="size-3.5 flex-shrink-0"
              style={{ color: "#818cf8" }}
            />
            <Dropdown
              size="sm"
              variant="pill"
              value={filterGroup}
              onChange={(v) => handleGroupFilter(v as FilterGroup)}
              options={[
                { value: "all", label: "All Types" },
                { value: "components", label: "Components" },
                { value: "services", label: "Service Deps" },
                { value: "cloud", label: "Cloud Accounts" },
              ]}
            />
          </div>

          {filterGroup !== "all" && itemOptions.length > 0 && (
            <>
              <ChevronRight
                className="size-3.5 flex-shrink-0"
                style={{ color: "#1e3a5f" }}
              />

              {/* Filter Item */}
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {filterGroup === "components" && (
                  <Server
                    className="size-3.5 flex-shrink-0"
                    style={{ color: "#38bdf8" }}
                  />
                )}
                {filterGroup === "services" && (
                  <GitBranch
                    className="size-3.5 flex-shrink-0"
                    style={{ color: "#a855f7" }}
                  />
                )}
                {filterGroup === "cloud" && (
                  <Cloud
                    className="size-3.5 flex-shrink-0"
                    style={{ color: "#f59e0b" }}
                  />
                )}
                <Dropdown
                  size="sm"
                  variant="pill"
                  value={filterItem}
                  onChange={(v) => handleItemFilter(v)}
                  options={[
                    {
                      value: "all",
                      label:
                        filterGroup === "components"
                          ? "All Components"
                          : filterGroup === "services"
                            ? "All Service Deps"
                            : "All Cloud Accounts",
                    },
                    ...itemOptions.map((opt) => ({
                      value: opt,
                      label: opt.length > 28 ? opt.slice(0, 26) + "…" : opt,
                    })),
                  ]}
                />
              </div>
            </>
          )}

          {/* Clear filters */}
          {filtersActive && (
            <button
              onClick={handleClearFilters}
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 6,
                border: "1px solid rgba(239,68,68,0.3)",
                background: "rgba(239,68,68,0.1)",
                color: "#f87171",
                cursor: "pointer",
                marginLeft: 4,
              }}
            >
              Clear Filters
            </button>
          )}

          {/* Hint */}
          <span style={{ fontSize: 10, color: "#1e3a5f", marginLeft: "auto" }}>
            {filtersActive
              ? `Showing filtered view · ${nodes.length} nodes`
              : "Select type → item to focus a branch"}
          </span>
        </div>

        {/* Row 3 — legend */}
        <div
          className="flex items-center gap-2 px-4 py-2 flex-wrap"
          style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)"}` }}
        >
          {[
            { label: "App", color: "#818cf8" },
            { label: "Component", color: "#38bdf8" },
            { label: "Service Dependency", color: "#a855f7" },
            { label: "Cloud Account", color: "#f59e0b" },
            { label: "Direct Resource", color: "#fb923c" },
            { label: "Deployment Node", color: "#64748b" },
          ].map((item) => (
            <span
              key={item.label}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                color: isDark ? "#94a3b8" : "#475569",
                padding: "3px 8px",
                borderRadius: 999,
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#e2e8f0"}`,
                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: item.color,
                  flexShrink: 0,
                }}
              />
              {item.label}
            </span>
          ))}
          <span style={{ marginLeft: "auto", fontSize: 10, color: "#64748b" }}>
            Lines show relationship flow from Application to linked entities.
          </span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: AT_GLOBAL_CSS }} />
      <ReactFlow
        className="at-flow"
        nodes={nodes}
        edges={edges}
        onNodesChange={locked ? undefined : onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={atNodeTypes}
        nodesDraggable={!locked}
        onInit={(instance) => {
          setFlowRef(instance);
          setTimeout(
            () => instance.fitView({ padding: 0.25, duration: 400 }),
            120,
          );
        }}
        minZoom={0.05}
        maxZoom={2.5}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background
          color={isDark ? "#1a2e44" : "#f8fafc"}
          gap={32}
          size={isDark ? 1.2 : 1}
          variant={isDark ? ("dots" as any) : ("lines" as any)}
        />
        <MiniMap
          nodeColor={(n) =>
            n.type === "atCompNode" || n.type === "atSvcCompNode"
              ? "#38bdf8"
              : n.type === "atSvcDepNode"
                ? "#a855f7"
                : n.type === "atCloudAccNode"
                  ? "#f59e0b"
                  : n.type === "atDirectResNode"
                    ? "#fb923c"
                    : n.type === "atDeployNode"
                      ? "#64748b"
                      : "#818cf8"
          }
          maskColor={
            isDark ? "rgba(6,16,30,0.85)" : "rgba(241,245,249,0.85)"
          }
          style={{
            background: isDark ? "rgba(6,16,30,0.92)" : "#ffffff",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
            borderRadius: 10,
            bottom: 16,
            right: 60,
          }}
          zoomable
          pannable
        />
      </ReactFlow>

      {/* Floating control panel — matches the Cloud Accounts topology view */}
      <AtTopologyControls
        onZoomIn={() => flowRef?.zoomIn({ duration: 200 })}
        onZoomOut={() => flowRef?.zoomOut({ duration: 200 })}
        onFitView={() => flowRef?.fitView({ padding: 0.2, duration: 400 })}
        onFullscreen={toggleFullscreen}
        locked={locked}
        onToggleLock={() => setLocked((l) => !l)}
        isFullscreen={isFullscreen}
        isDark={isDark}
      />
    </div>
  );
}
